import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';
import { ChevronRightIcon, SpinnerIcon } from '../../components/ui/Icons';
import type { Murmur } from '../../types';

const Murmurs: React.FC = () => {
    const [murmurs, setMurmurs] = useState<Murmur[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // Date range boundaries: max = today, min = 3 months ago
    const todayStr = new Date().toISOString().split('T')[0];
    const threeMonthsAgoObj = new Date();
    threeMonthsAgoObj.setMonth(threeMonthsAgoObj.getMonth() - 3);
    const minDateStr = threeMonthsAgoObj.toISOString().split('T')[0];

    // Default start date (last 1 week)
    const sevenDaysAgoObj = new Date();
    sevenDaysAgoObj.setDate(sevenDaysAgoObj.getDate() - 7);
    const defaultStartDateStr = sevenDaysAgoObj.toISOString().split('T')[0];

    // Filters
    const [startDate, setStartDate] = useState(defaultStartDateStr);
    const [endDate, setEndDate] = useState(todayStr);
    const [symbolFilter, setSymbolFilter] = useState('');
    const [minPrice, setMinPrice] = useState('');
    const [maxPrice, setMaxPrice] = useState('');
    const [minValue, setMinValue] = useState('10');

    // Sorting
    const [sortBy, setSortBy] = useState('timestamp');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    
    // Pagination
    const [page, setPage] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const limit = 20;

    const fetchMurmurs = async (
        currentPage = page, 
        startVal = startDate, 
        endVal = endDate,
        symbolVal = symbolFilter,
        minPriceVal = minPrice,
        maxPriceVal = maxPrice,
        minValueVal = minValue,
        sortVal = sortBy,
        orderVal = sortOrder
    ) => {
        setLoading(true);
        setError(null);
        try {
            const params: any = {
                limit,
                offset: currentPage * limit,
                sort_by: sortVal,
                sort_order: orderVal
            };
            if (startVal) params.start_date = startVal;
            if (endVal) params.end_date = endVal;
            if (symbolVal) params.symbol = symbolVal.trim().toUpperCase();
            if (minPriceVal) params.min_price = minPriceVal;
            if (maxPriceVal) params.max_price = maxPriceVal;
            if (minValueVal !== '') params.min_value = minValueVal;

            const response = await apiService.murmurs.get(params);
            if (response.success && response.data) {
                setMurmurs(response.data);
                if (response.pagination) {
                    setTotalCount(response.pagination.total);
                }
            } else {
                setError(response.error?.message || 'Failed to fetch murmurs');
            }
        } catch (err) {
            console.error('Error loading murmurs:', err);
            setError('Failed to load murmurs. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMurmurs(page, startDate, endDate, symbolFilter, minPrice, maxPrice, minValue, sortBy, sortOrder);
    }, [page, sortBy, sortOrder]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setPage(0);
        fetchMurmurs(0, startDate, endDate, symbolFilter, minPrice, maxPrice, minValue, sortBy, sortOrder);
    };

    const handleClearFilters = () => {
        setStartDate(defaultStartDateStr);
        setEndDate(todayStr);
        setSymbolFilter('');
        setMinPrice('');
        setMaxPrice('');
        setMinValue('10');
        setPage(0);
        fetchMurmurs(0, defaultStartDateStr, todayStr, '', '', '', '10', sortBy, sortOrder);
    };

    const handleSort = (field: string) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('desc'); // Default to descending on column switch
        }
        setPage(0);
    };

    const formatTimestamp = (ts: string) => {
        const dateObj = new Date(ts);
        return dateObj.toLocaleString('en-IN', {
            timeZone: 'Asia/Kolkata',
            dateStyle: 'medium',
            timeStyle: 'medium'
        });
    };

    const formatVolume = (vol?: number) => {
        if (!vol) return '-';
        return Math.round(vol).toLocaleString('en-IN');
    };

    const renderSortIndicator = (field: string) => {
        if (sortBy !== field) return null;
        return (
            <span className="inline-block ml-1 text-trade-primary font-bold">
                {sortOrder === 'asc' ? '▲' : '▼'}
            </span>
        );
    };

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in">
            {/* Breadcrumbs */}
            <nav className="flex items-center space-x-2 text-sm text-slate-500 mb-6">
                <Link to="/grow" className="hover:text-trade-primary transition-colors">Grow</Link>
                <ChevronRightIcon className="w-4 h-4 text-slate-400" />
                <span className="text-slate-900 font-medium">Murmurs</span>
            </nav>

            <div className="mb-8">
                <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Murmurs</h1>
                <p className="text-slate-600 mt-2 text-lg">
                    Real-time identification of short interval volume spurts indicating institutional buying.
                </p>
            </div>

            {/* Filter Panel */}
            <form onSubmit={handleSearch} className="bg-white rounded-xl shadow-sm border border-slate-100 p-6 mb-8 flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Stock Symbol */}
                    <div>
                        <label htmlFor="symbol" className="block text-sm font-semibold text-slate-700 mb-1">
                            Stock Symbol
                        </label>
                        <input
                            type="text"
                            id="symbol"
                            value={symbolFilter}
                            onChange={(e) => setSymbolFilter(e.target.value)}
                            placeholder="e.g. LLOYDSENGG"
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trade-primary focus:border-transparent text-sm bg-slate-50 text-slate-900 placeholder-slate-400 font-medium"
                        />
                    </div>
                    {/* Start Date */}
                    <div>
                        <label htmlFor="startDate" className="block text-sm font-semibold text-slate-700 mb-1">
                            Start Date
                        </label>
                        <input
                            type="date"
                            id="startDate"
                            value={startDate}
                            min={minDateStr}
                            max={endDate || todayStr}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trade-primary focus:border-transparent text-sm bg-slate-50 text-slate-900 font-medium"
                        />
                    </div>
                    {/* End Date */}
                    <div>
                        <label htmlFor="endDate" className="block text-sm font-semibold text-slate-700 mb-1">
                            End Date
                        </label>
                        <input
                            type="date"
                            id="endDate"
                            value={endDate}
                            min={startDate || minDateStr}
                            max={todayStr}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trade-primary focus:border-transparent text-sm bg-slate-50 text-slate-900 font-medium"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-slate-100 pt-4">
                    {/* Min Price */}
                    <div>
                        <label htmlFor="minPrice" className="block text-sm font-semibold text-slate-700 mb-1">
                            Min Price (₹)
                        </label>
                        <input
                            type="number"
                            id="minPrice"
                            value={minPrice}
                            min="0"
                            step="any"
                            onChange={(e) => setMinPrice(e.target.value)}
                            placeholder="Min Price"
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trade-primary focus:border-transparent text-sm bg-slate-50 text-slate-900 placeholder-slate-400 font-medium"
                        />
                    </div>
                    {/* Max Price */}
                    <div>
                        <label htmlFor="maxPrice" className="block text-sm font-semibold text-slate-700 mb-1">
                            Max Price (₹)
                        </label>
                        <input
                            type="number"
                            id="maxPrice"
                            value={maxPrice}
                            min="0"
                            step="any"
                            onChange={(e) => setMaxPrice(e.target.value)}
                            placeholder="Max Price"
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trade-primary focus:border-transparent text-sm bg-slate-50 text-slate-900 placeholder-slate-400 font-medium"
                        />
                    </div>
                    {/* Min Value (Crores) */}
                    <div>
                        <label htmlFor="minValue" className="block text-sm font-semibold text-slate-700 mb-1">
                            Min Value (Crores)
                        </label>
                        <input
                            type="number"
                            id="minValue"
                            value={minValue}
                            min="0"
                            step="any"
                            onChange={(e) => setMinValue(e.target.value)}
                            placeholder="e.g. 10"
                            className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-trade-primary focus:border-transparent text-sm bg-slate-50 text-slate-900 placeholder-slate-400 font-medium"
                        />
                    </div>
                </div>

                <div className="flex justify-end space-x-2 border-t border-slate-100 pt-4">
                    <button
                        type="submit"
                        className="px-6 py-2 bg-trade-primary hover:bg-blue-700 text-white rounded-lg font-medium text-sm transition-colors shadow-sm"
                    >
                        Search
                    </button>
                    {(startDate !== defaultStartDateStr || endDate !== todayStr || symbolFilter || minPrice !== '' || maxPrice !== '' || minValue !== '10') && (
                        <button
                            type="button"
                            onClick={handleClearFilters}
                            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-medium text-sm transition-colors"
                        >
                            Reset
                        </button>
                    )}
                </div>
            </form>

            {loading ? (
                <div className="flex justify-center items-center py-24 bg-white rounded-2xl border border-slate-100 shadow-sm">
                    <SpinnerIcon className="animate-spin h-8 w-8 text-trade-primary" />
                </div>
            ) : error ? (
                <div className="bg-red-50 border border-red-100 text-red-700 p-4 rounded-xl shadow-sm text-center">
                    <p className="font-semibold mb-1">An error occurred</p>
                    <p className="text-sm">{error}</p>
                </div>
            ) : murmurs.length === 0 ? (
                <div className="text-center py-16 bg-slate-50 rounded-2xl border border-slate-100">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-100 text-blue-600 mb-4">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">No Murmurs Found</h2>
                    <p className="text-slate-600 max-w-md mx-auto">
                        We couldn't find any murmurs matching your filters. Try clearing your filters or check back later.
                    </p>
                </div>
            ) : (
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-slate-100 bg-slate-50 text-slate-500 font-semibold text-xs uppercase tracking-wider select-none">
                                    <th 
                                        className="px-6 py-4 cursor-pointer hover:bg-slate-100/80 transition-colors"
                                        onClick={() => handleSort('timestamp')}
                                    >
                                        Timestamp (IST) {renderSortIndicator('timestamp')}
                                    </th>
                                    <th className="px-6 py-4">Stock</th>
                                    <th 
                                        className="px-6 py-4 text-right cursor-pointer hover:bg-slate-100/80 transition-colors"
                                        onClick={() => handleSort('price')}
                                    >
                                        Price {renderSortIndicator('price')}
                                    </th>
                                    <th 
                                        className="px-6 py-4 text-right cursor-pointer hover:bg-slate-100/80 transition-colors"
                                        onClick={() => handleSort('value_crores')}
                                    >
                                        Value (Crores) {renderSortIndicator('value_crores')}
                                    </th>
                                    <th 
                                        className="px-6 py-4 text-right cursor-pointer hover:bg-slate-100/80 transition-colors"
                                        onClick={() => handleSort('volume')}
                                    >
                                        Volume (Shares) {renderSortIndicator('volume')}
                                    </th>
                                    <th className="px-6 py-4 text-right">Minute Multiple</th>
                                    <th className="px-6 py-4 text-right">Projected Multiplier</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                                {murmurs.map((m) => (
                                    <tr key={m.id} className="hover:bg-slate-50/50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap font-medium text-slate-500">
                                            {formatTimestamp(m.timestamp)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className="font-bold text-slate-900 bg-slate-100 px-2.5 py-1 rounded-md text-xs tracking-wider">
                                                {m.symbol}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-right whitespace-nowrap font-bold text-slate-900">
                                            ₹{parseFloat(m.price.toString()).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-6 py-4 text-right whitespace-nowrap font-bold text-emerald-600">
                                            ₹{parseFloat(m.value_crores.toString()).toFixed(2)} Cr
                                        </td>
                                        <td className="px-6 py-4 text-right whitespace-nowrap font-medium text-slate-600">
                                            {formatVolume(m.volume)}
                                        </td>
                                        <td className="px-6 py-4 text-right whitespace-nowrap font-medium text-slate-600">
                                            {m.minute_multiple ? parseFloat(m.minute_multiple.toString()).toFixed(2) : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-right whitespace-nowrap font-medium text-slate-600">
                                            {m.projected_multiplier ? parseFloat(m.projected_multiplier.toString()).toFixed(2) : '-'}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalCount > limit && (
                        <div className="bg-slate-50 border-t border-slate-100 px-6 py-4 flex items-center justify-between">
                            <button
                                onClick={() => setPage(p => Math.max(p - 1, 0))}
                                disabled={page === 0}
                                className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-semibold bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                            >
                                Previous
                            </button>
                            <span className="text-sm font-medium text-slate-500">
                                Page {page + 1} of {Math.ceil(totalCount / limit)} ({totalCount} total)
                            </span>
                            <button
                                onClick={() => setPage(p => p + 1)}
                                disabled={(page + 1) * limit >= totalCount}
                                className="px-4 py-2 border border-slate-200 rounded-lg text-sm font-semibold bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default Murmurs;
